import { Header } from "@/app/layout/Header";
import { Main } from "@/app/layout/Main";
import { useActionPermission } from "@/modules/auth/presentation/model/useActionPermission";
import { Breadcrumb } from "@/shared/ui/Breadcrumb";
import { SimpleSelect } from "@/shared/ui/SimpleSelect";
import { Badge, Button, Input, confirm, toast } from "@devhop/ui";
import { useNavigate, useSearch } from "@tanstack/react-router";
import { DownloadIcon, Trash2Icon } from "lucide-react";
import { useMemo, useState } from "react";
import { visitorLotteryApi } from "../api/client";
import {
  useDeleteVisitorRegistrationMutation,
  useRedeemVisitorRegistrationMutation,
  useVisitorLotteryEventQuery,
  useVisitorLotteryEventsQuery,
  useVisitorRegistrationsQuery,
} from "../api/queries";

type Search = {
  limit?: number;
  offset?: number;
  eventId?: string;
  prizeId?: string;
  redeemed?: string;
  won?: string;
  search?: string;
};

export function VisitorRegistrationsPage() {
  const nav = useNavigate({ from: "/app/visitor-lottery/registrations" });
  const canWrite = useActionPermission(["visitor-lottery:write"]);
  const canExport = useActionPermission(["visitor-lottery:export"]);
  const search = useSearch({
    from: "/app/visitor-lottery/registrations",
  }) as Search;

  const { data: eventsData } = useVisitorLotteryEventsQuery();
  const eventId = String(search.eventId ?? eventsData?.items[0]?.id ?? "");
  const { data: eventDetail } = useVisitorLotteryEventQuery(
    eventId || undefined,
  );

  const [searchInput, setSearchInput] = useState(search.search ?? "");

  const query = useMemo(
    () => ({
      limit: Number(search.limit ?? 20),
      offset: Number(search.offset ?? 0),
      eventId: eventId || undefined,
      prizeId: search.prizeId,
      redeemed: search.redeemed,
      won: search.won,
      search: search.search,
    }),
    [search, eventId],
  );

  const exportQuery = useMemo(
    () => ({
      eventId,
      prizeId: search.prizeId,
      redeemed: search.redeemed,
      won: search.won,
      search: search.search,
    }),
    [eventId, search.prizeId, search.redeemed, search.won, search.search],
  );

  const { data, isLoading } = useVisitorRegistrationsQuery(query);
  const redeemMutation = useRedeemVisitorRegistrationMutation();
  const deleteMutation = useDeleteVisitorRegistrationMutation();
  const items = data?.data ?? [];
  const total = data?.meta?.total ?? 0;

  const handleDelete = async (row: {
    id: string;
    fullName: string;
    phoneNumber: string;
    prizeName?: string | null;
    redeemCode?: string | null;
  }) => {
    const willRestoreStock = Boolean(row.redeemCode);
    const ok = await confirm({
      title: "Delete registration",
      description: willRestoreStock
        ? `Delete "${row.fullName}" (${row.phoneNumber})? Prize "${row.prizeName ?? "-"}" will be returned to stock.`
        : `Delete "${row.fullName}" (${row.phoneNumber})? This cannot be undone.`,
    });
    if (!ok) return;
    try {
      const result = await deleteMutation.mutateAsync(row.id);
      toast.success(
        result.stockRestored
          ? "Deleted — prize returned to stock"
          : "Registration deleted",
      );
    } catch {
      toast.error("Delete failed");
    }
  };


  const setSearchParams = (patch: Partial<Search>) => {
    nav({
      search: (prev) => ({
        ...(prev as Search),
        ...patch,
        offset: patch.offset ?? 0,
      }),
    });
  };

  const downloadBlob = (blob: Blob, filename: string) => {
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = filename;
    a.click();
    URL.revokeObjectURL(url);
  };

  const handleExportCsv = async () => {
    if (!eventId) return;
    try {
      const blob = await visitorLotteryApi.exportCsv(exportQuery);
      downloadBlob(blob, `visitor-lottery-${eventDetail?.slug ?? eventId}.csv`);
    } catch {
      toast.error("Export ບໍ່ສຳເລັດ");
    }
  };

  const handleExportNames = async () => {
    if (!eventId) return;
    try {
      const blob = await visitorLotteryApi.exportNames(exportQuery);
      downloadBlob(
        blob,
        `visitor-lottery-names-${eventDetail?.slug ?? eventId}.txt`,
      );
    } catch {
      toast.error("Export ບໍ່ສຳເລັດ");
    }
  };

  return (
    <>
      <Header>
        <Breadcrumb
          items={[
            { label: "Visitor Lottery", href: "/app/visitor-lottery" },
            { label: "ລາຍຊື່ຜູ້ເຂົ້າຊົມ" },
          ]}
        />
      </Header>
      <Main>
        <div className="mb-6 flex flex-wrap items-center justify-between gap-3">
          <div>
            <h1 className="text-2xl font-semibold">ລາຍຊື່ຜູ້ເຂົ້າຊົມ</h1>
            <p className="text-muted-foreground text-sm">
              {eventDetail?.title ?? "Visitor Lottery"} · {total} ຄົນ
            </p>
          </div>
          {canExport && eventId ? (
            <div className="flex flex-wrap gap-2">
              <Button variant="outline" onClick={handleExportNames}>
                <DownloadIcon className="mr-2 size-4" />
                Export ລາຍຊື່
              </Button>
              <Button variant="outline" onClick={handleExportCsv}>
                <DownloadIcon className="mr-2 size-4" />
                ສົ່ງອອກ CSV
              </Button>
            </div>
          ) : null}
        </div>

        <div className="mb-4 flex flex-wrap gap-2">
          <Input
            className="max-w-xs"
            placeholder="ຄົ້ນຫາຊື່ / ເບີ / ໂຄດ"
            value={searchInput}
            onChange={(e) => setSearchInput(e.target.value)}
            onKeyDown={(e) => {
              if (e.key === "Enter") {
                setSearchParams({ search: searchInput || undefined });
              }
            }}
          />
          <Button
            variant="secondary"
            onClick={() =>
              setSearchParams({ search: searchInput || undefined })
            }
          >
            ຄົ້ນຫາ
          </Button>
          <SimpleSelect
            value={search.won ?? "all"}
            onValueChange={(v) =>
              setSearchParams({ won: v === "all" ? undefined : v })
            }
            options={[
              { value: "all", label: "ທຸກສະຖານະລາງວັນ" },
              { value: "no", label: "ຍັງບໍ່ຖືກລາງວັນ" },
              { value: "yes", label: "ຖືກລາງວັນແລ້ວ" },
            ]}
          />
          <SimpleSelect
            value={search.redeemed ?? "all"}
            onValueChange={(v) =>
              setSearchParams({ redeemed: v === "all" ? undefined : v })
            }
            options={[
              { value: "all", label: "ທັງໝົດ" },
              { value: "no", label: "ຍັງບໍ່ຮັບ" },
              { value: "yes", label: "ຮັບແລ້ວ" },
            ]}
          />
          <SimpleSelect
            value={search.prizeId ?? "all"}
            onValueChange={(v) =>
              setSearchParams({ prizeId: v === "all" ? undefined : v })
            }
            options={[
              { value: "all", label: "ທຸກລາງວັນ" },
              ...(eventDetail?.prizes ?? []).map((p) => ({
                value: p.id,
                label: p.name,
              })),
            ]}
          />
        </div>

        <div className="overflow-x-auto rounded-md border">
          <table className="w-full text-sm">
            <thead className="bg-muted/50 text-left">
              <tr>
                <th className="p-2">ເວລາ</th>
                <th className="p-2">ຊື່</th>
                <th className="p-2">ເບີ</th>
                <th className="p-2">ລາງວັນ</th>
                <th className="p-2">ໂຄດ</th>
                <th className="p-2">ຮັບຂອງ</th>
                <th className="p-2" />
              </tr>
            </thead>
            <tbody>
              {isLoading ? (
                <tr>
                  <td className="text-muted-foreground p-4" colSpan={7}>
                    ກຳລັງໂຫຼດ...
                  </td>
                </tr>
              ) : items.length === 0 ? (
                <tr>
                  <td className="text-muted-foreground p-4" colSpan={7}>
                    ຍັງບໍ່ມີຂໍ້ມູນ
                  </td>
                </tr>
              ) : (
                items.map((row) => (
                  <tr key={row.id} className="border-t">
                    <td className="p-2 whitespace-nowrap">{row.submittedAt}</td>
                    <td className="p-2">
                      {row.fullName}
                      {row.nickname ? (
                        <span className="text-muted-foreground">
                          {" "}
                          ({row.nickname})
                        </span>
                      ) : null}
                    </td>
                    <td className="p-2 font-mono text-xs">{row.phoneNumber}</td>
                    <td className="p-2">{row.prizeName ?? "—"}</td>
                    <td className="p-2 font-mono text-xs">
                      {row.redeemCode ?? "—"}
                    </td>
                    <td className="p-2">
                      {row.redeemedAt ? (
                        <Badge variant="default">ຮັບແລ້ວ</Badge>
                      ) : row.redeemCode ? (
                        <Badge variant="secondary">ລໍຖ້າ</Badge>
                      ) : (
                        <Badge variant="outline">—</Badge>
                      )}
                    </td>
                    <td className="p-2 text-right">
                      <div className="flex justify-end gap-2">
                        {canWrite && row.redeemCode && !row.redeemedAt ? (
                          <Button
                            size="sm"
                            variant="outline"
                            disabled={
                              redeemMutation.isPending ||
                              deleteMutation.isPending
                            }
                            onClick={async () => {
                              try {
                                await redeemMutation.mutateAsync(row.id);
                                toast.success("ໝາຍວ່າຮັບຂອງແລ້ວ");
                              } catch {
                                toast.error('ບໍ່ສາມາດໝາຍໄດ້');
                              }
                            }}
                          >
                            Mark redeemed
                          </Button>
                        ) : null}
                        {canWrite ? (
                          <Button
                            size="sm"
                            variant="ghost"
                            className="text-destructive hover:text-destructive"
                            disabled={deleteMutation.isPending}
                            onClick={() => void handleDelete(row)}
                            title="Delete"
                          >
                            <Trash2Icon className="size-4" />
                          </Button>
                        ) : null}
                      </div>
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>

        <div className="mt-4 flex items-center justify-between">
          <p className="text-muted-foreground text-sm">
            {query.offset + 1}–{Math.min(query.offset + query.limit, total)} /{" "}
            {total}
          </p>
          <div className="flex gap-2">
            <Button
              variant="outline"
              size="sm"
              disabled={query.offset <= 0}
              onClick={() =>
                setSearchParams({
                  offset: Math.max(0, query.offset - query.limit),
                })
              }
            >
              ກ່ອນໜ້າ
            </Button>
            <Button
              variant="outline"
              size="sm"
              disabled={query.offset + query.limit >= total}
              onClick={() =>
                setSearchParams({ offset: query.offset + query.limit })
              }
            >
              ຖັດໄປ
            </Button>
          </div>
        </div>
      </Main>
    </>
  );
}
